import { Store }                from './state/Store.js';
import { initialGameState, normaliseGameState } from './state/gameState.js';
import { initialLocalState, saveLocal, loadLocal } from './state/localState.js';
import { storage }             from './services/storage.js';
import * as firebase           from './services/firebase.js';
import { Nav }                 from './components/layout/Nav.js';
import { TeamView }            from './components/views/TeamView.js';
import { FacilitatorView }     from './components/views/FacilitatorView.js';
import { ScorerView }          from './components/views/ScorerView.js';
import { BoardView }           from './components/views/BoardView.js';
import { validateFirebaseConfig, validateTeamSetup } from './utils/validation.js';
import { fk, teamLabel, generateSessionCode, normaliseSessionCode, SESSION_CODE_LENGTH } from './utils/helpers.js';
import { TEAM_NAMES, SCORER_TEAMS, PRIVACY, applyContentPack } from './config/content.js';
import { BRAND, applyBrand }   from './config/brand.js';
import { ContentEditor }       from './components/ContentEditor.js';

const ROLE_DEFAULT_VIEW = { team:'team', facilitator:'facilitator', scorer:'scorer', board:'board', master:'facilitator' };

export class App {
  #gameStore  = new Store(initialGameState);
  #localStore = new Store(initialLocalState);
  #views      = {};
  #nav        = null;
  #timerIv    = null;
  #unsub      = null;
  #unsubConn  = null;
  #viewsReady = false;
  #lastTeamSig = null;
  #connEverOnline   = false;
  #connOfflineTimer = null;

  async init() {
    this.#setupErrorHandler();

    const savedLocal = loadLocal(storage);
    if (savedLocal) this.#localStore.setState(savedLocal);

    // Try window injection (Cloudflare Pages) → then localStorage
    const cfgSrc = window.__FIREBASE_CFG__ ?? storage.get('mv_cfg');
    if (cfgSrc?.apiKey && cfgSrc?.databaseURL) {
      storage.set('mv_cfg', cfgSrc);
      await this.#connect(cfgSrc);
    } else {
      this.#showLoading(false);
      this.#showOverlay('config');
    }
  }

  async #connect(cfg) {
    this.#showLoading(true);
    try {
      firebase.initFirebase(cfg);
      this.#unsubConn?.();
      this.#unsubConn = firebase.subscribeToConnection(on => this.#updateConnStatus(on));
      // Rejoin the session this device was in, if it still exists;
      // otherwise ask for a join code.
      const saved = storage.get('mv_session');
      if (saved) {
        let exists = false;
        try {
          exists = await firebase.sessionExists(saved);
        } catch (e) {
          // Database unreachable (offline, or App Check rejecting this client).
          // Keep mv_session — a transient outage shouldn't kick the device out
          // of its workshop — and surface the problem on the join overlay.
          console.error('[App] saved-session check failed:', e);
          this.#showOverlay('session', 'Could not reach the database — check your connection and try again.');
          return;
        }
        if (exists) {
          await this.#enterSession(saved);
          return;
        }
      }
      storage.remove('mv_session');
      this.#showOverlay('session');
    } catch (e) {
      console.error('[App] Firebase connect error:', e);
      this.#showOverlay('config', 'Connection failed: ' + e.message);
    } finally {
      this.#showLoading(false);
    }
  }

  /**
   * Scope this device to a session and start (or restart) the live game
   * subscription. Safe to call again when switching sessions.
   */
  async #enterSession(code) {
    if (storage.get('mv_session') !== code) {
      // A genuinely different session from whatever this device was last in —
      // a different workshop is a different identity (same as #switchSession),
      // so drop any remembered role/team rather than silently re-applying it.
      storage.remove('mv_draft');
      this.#localStore.setState({ ...initialLocalState });
      saveLocal(this.#localStore.getState(), storage);
    }
    firebase.setSession(code);
    storage.set('mv_session', code);

    // Load this session's content pack (falls back to the deployment-wide
    // pack, then built-ins). Failure is non-fatal.
    try {
      applyContentPack(await firebase.readContentPack());
      applyBrand();
    } catch (e) {
      console.warn('[App] content pack load failed — using built-in defaults:', e);
    }

    this.#unsub?.();
    this.#gameStore.reset(initialGameState);
    this.#lastTeamSig = null;
    this.#unsub = firebase.subscribeToGame(raw => {
      const next = normaliseGameState(raw);
      const prev = this.#gameStore.getState();
      this.#gameStore.setState(next);
      this.#onGameUpdate(next, prev);
    });

    document.getElementById('app-shell').hidden = false;
    if (!this.#viewsReady) {
      this.#initViews();
      this.#initNav();
      this.#viewsReady = true;
    }
    const L = this.#localStore.getState();
    if (L.role) this.#applyRole();
    else        this.#showOverlay('role');
  }

  #initViews() {
    const deps = { gameStore: this.#gameStore, localStore: this.#localStore, firebase };
    this.#views = {
      team:        new TeamView(document.getElementById('view-team'), deps),
      facilitator: new FacilitatorView(document.getElementById('view-facilitator'), { gameStore: this.#gameStore, firebase }),
      scorer:      new ScorerView(document.getElementById('view-scorer'), deps),
      board:       new BoardView(document.getElementById('view-board'), { gameStore: this.#gameStore }),
    };
    // First-time "Set up team" button (shown only when this device has no team yet)
    document.getElementById('view-team')?.addEventListener('team:setup', () => this.#showOverlay('team'));
    document.addEventListener('app:error', e => this.#showToast(e.detail?.message ?? 'An error occurred.', 'error'));
    document.addEventListener('app:info',  e => this.#showToast(e.detail?.message ?? '', 'success'));
    document.addEventListener('content:edit', () => {
      if (this.#canFacilitate()) this.#showOverlay('contentpack');
    });
    document.addEventListener('session:switch', () => this.#switchSession());
  }

  #initNav() {
    this.#nav = new Nav(document.getElementById('app-nav'), view => this.#showView(view));
    this.#nav.mount();
  }

  #applyRole() {
    const L        = this.#localStore.getState();
    const defView  = ROLE_DEFAULT_VIEW[L.role] ?? 'board';
    this.#nav.setRole(L.role, defView);
    this.#setupMasterSwitcher(L);
    this.#showView(defView);
  }

  #showView(viewName) {
    Object.entries(this.#views).forEach(([name, view]) => {
      const el = document.getElementById(`view-${name}`);
      if (el) el.hidden = name !== viewName;
    });
    this.#nav.setActiveView(viewName);
    if (viewName === 'team') {
      // Keep the render signature in sync so the next snapshot doesn't
      // trigger a redundant remount right after this explicit one
      this.#lastTeamSig = this.#teamRenderSig(this.#gameStore.getState(), this.#localStore.getState());
    }
    this.#views[viewName]?.mount();
  }

  #onGameUpdate(G, prev) {
    const L = this.#localStore.getState();

    // Global reset signal — bounce all connected devices
    if (G._reset && G._reset !== prev._reset) {
      if (G._reset !== L._lastReset) {
        this.#stopTimer();
        storage.remove('mv_draft');
        this.#localStore.setState({ ...initialLocalState, _lastReset: G._reset });
        saveLocal(this.#localStore.getState(), storage);
        this.#showOverlay('role');
        return;
      }
    }

    // Facilitator returned this device's team registration — reopen Team Setup
    if (L.role === 'team' && L.team && !G.teams?.[fk(L.team)]) {
      const overlay = document.getElementById('overlay-team');
      if (overlay?.hidden) { this.#showOverlay('team'); return; }
    }

    // Timer management
    if (G.active && !this.#timerIv) this.#startTimer();
    if (!G.active)                  this.#stopTimer();

    // Bomb drop — reveal section on team form if visible
    if (G.bomb && !prev.bomb) {
      const el = document.getElementById('t-bombSection');
      if (el) el.hidden = false;
    }

    // Re-render active view
    if (!L.role) return;
    const defView = ROLE_DEFAULT_VIEW[L.role] ?? 'board';
    const activeView = this.#nav?.props?.activeView ?? defView;
    if (activeView === 'team') {
      // Mounting TeamView replaces its innerHTML, which dismisses the iPad
      // keyboard and interrupts in-progress signatures — only re-render when
      // a slice of state this team's screen actually shows has changed, not
      // on every snapshot (other teams submitting, scorers saving, etc.)
      const sig = this.#teamRenderSig(G, L);
      if (sig !== this.#lastTeamSig) {
        this.#lastTeamSig = sig;
        this.#views.team?.mount();
      }
    } else {
      this.#views[activeView]?.mount();
    }
    // Always keep board fresh
    if (activeView !== 'board') this.#views.board?.mount();
  }

  /** Serialised snapshot of the game-state slices TeamView renders for this
   *  device's team. Values (not object references) — every Firebase snapshot
   *  produces fresh objects, so reference equality would always differ. */
  #teamRenderSig(G, L) {
    if (!L.team) return JSON.stringify({ team: null, teams: Object.keys(G.teams ?? {}) });
    const k = fk(L.team);
    return JSON.stringify({
      team:   L.team,
      round:  G.round,
      active: G.active,
      bomb:   G.bomb,
      sub:    !!G.subs[`${k}_R${G.round}`],
      ret:    G.returned[`${k}_R${G.round}`] ?? 0,
      badges: G.badges[k] ?? null,
      scores: [0, 1, 2].map(r => G.scores[`${k}_R${r}`] ?? null),
      reg:    G.teams[k] ?? null,
    });
  }

  // ── Timer ────────────────────────────────────────────────────────────────
  #startTimer() {
    if (this.#timerIv) return;
    this.#timerIv = setInterval(() => this.#tick(), 500);
    this.#tick();
  }

  #stopTimer() {
    clearInterval(this.#timerIv);
    this.#timerIv = null;
  }

  #tick() {
    const G    = this.#gameStore.getState();
    const rem  = Math.max(0, Math.round((G.endMs - Date.now()) / 1000));
    const tot  = Math.round(G.durMs / 1000);
    const pct  = tot > 0 ? (rem / tot * 100) : 0;
    const m    = Math.floor(rem / 60);
    const s    = rem % 60;
    const str  = `${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
    const cls  = rem < 60 ? 'danger' : rem < 180 ? 'warning' : '';

    ['t', 'f'].forEach(p => {
      const disp = document.getElementById(`${p}-timer`);
      const fill = document.getElementById(`${p}-tfill`);
      if (disp) {
        disp.textContent = str;
        disp.className   = `timer-display${cls ? ` timer-display--${cls}` : ''}`;
      }
      if (fill) {
        fill.style.width = pct + '%';
        fill.className   = `timer-fill${cls ? ` timer-fill--${cls}` : ''}`;
      }
    });
  }

  // ── Overlays ─────────────────────────────────────────────────────────────
  #showOverlay(name, errorMsg = '') {
    ['config','session','role','privacy','team','contentpack'].forEach(n => {
      const el = document.getElementById(`overlay-${n}`);
      if (el) el.hidden = n !== name;
    });
    document.getElementById('app-shell').hidden = true;

    if (name === 'config')      this.#mountConfigOverlay(errorMsg);
    if (name === 'session')     this.#mountSessionOverlay(errorMsg);
    if (name === 'role')        this.#mountRoleOverlay();
    if (name === 'privacy')     this.#mountPrivacyOverlay();
    if (name === 'team')        this.#mountTeamOverlay();
    if (name === 'contentpack') this.#mountContentEditor();

    // Focus management for accessibility
    requestAnimationFrame(() => {
      const overlay = document.getElementById(`overlay-${name}`);
      overlay?.querySelector('input, button')?.focus();
    });
  }

  #mountConfigOverlay(errorMsg = '') {
    const el = document.getElementById('overlay-config');
    if (!el) return;
    const saved = storage.get('mv_cfg') ?? {};
    el.innerHTML = `
      <div class="overlay__logo">${escHtml(BRAND.simName)}</div>
      <h1 class="overlay__title">Firebase Setup</h1>
      <div class="overlay__card">
        <h2 class="overlay__card-heading">Firebase config — one time only</h2>
        <input class="overlay__input" id="cfg-api"  placeholder="apiKey"       value="${saved.apiKey ?? ''}"       aria-label="Firebase API Key" autocomplete="off">
        <input class="overlay__input" id="cfg-auth" placeholder="authDomain"   value="${saved.authDomain ?? ''}"   aria-label="Firebase Auth Domain" autocomplete="off">
        <input class="overlay__input" id="cfg-db"   placeholder="databaseURL"  value="${saved.databaseURL ?? ''}"  aria-label="Firebase Database URL" autocomplete="off">
        <input class="overlay__input" id="cfg-proj" placeholder="projectId"    value="${saved.projectId ?? ''}"    aria-label="Firebase Project ID" autocomplete="off">
        ${errorMsg ? `<p class="overlay__error" role="alert">${errorMsg}</p>` : ''}
      </div>
      <div style="max-width:480px;width:100%">
        <button class="btn btn--primary" style="width:100%;justify-content:center;padding:12px;font-size:14px" id="cfg-submit">Connect to Firebase</button>
      </div>
    `;
    el.querySelector('#cfg-submit').addEventListener('click', () => this.#saveConfig());
    el.querySelector('#cfg-api').addEventListener('keydown', e => { if (e.key === 'Enter') this.#saveConfig(); });
  }

  async #saveConfig() {
    const get = id => document.getElementById(id)?.value.trim() ?? '';
    const cfg = { apiKey: get('cfg-api'), authDomain: get('cfg-auth'), databaseURL: get('cfg-db'), projectId: get('cfg-proj') };
    const { ok, errors } = validateFirebaseConfig(cfg);
    if (!ok) { this.#mountConfigOverlay(errors.join(', ')); return; }
    storage.set('mv_cfg', cfg);
    await this.#connect(cfg);
  }

  // ── Session join / create ─────────────────────────────────────────────────
  #mountSessionOverlay(errorMsg = '') {
    const el = document.getElementById('overlay-session');
    if (!el) return;
    const canFacilitate = this.#canFacilitate();
    el.innerHTML = `
      <div class="overlay__logo">${escHtml(BRAND.simName)}</div>
      <h1 class="overlay__title">Join a session</h1>
      <div class="overlay__card">
        <h2 class="overlay__card-heading">Enter the session code</h2>
        <input class="overlay__input overlay__input--code" id="sess-code" type="text"
          maxlength="9" autocomplete="off" autocapitalize="characters" spellcheck="false"
          placeholder="e.g. K7MPX3" aria-label="Session code" aria-required="true">
        <p class="muted" style="font-size:11px;margin-top:2px">Your facilitator announces this ${SESSION_CODE_LENGTH}-character code at the start of the workshop.</p>
        ${errorMsg ? `<p class="overlay__error" role="alert">${escHtml(errorMsg)}</p>` : ''}
        <button class="btn btn--primary" style="width:100%;justify-content:center;padding:12px;font-size:14px;margin-top:10px" id="sess-join">Join session</button>
      </div>
      ${canFacilitate ? `
      <div class="overlay__card">
        <h2 class="overlay__card-heading">🎯 Facilitators</h2>
        <p class="muted" style="font-size:11px;margin-bottom:10px">Start a new workshop — a fresh session with its own join code, game state and content pack.</p>
        <input class="overlay__input" id="sess-clientname" type="text" maxlength="80" autocomplete="off"
          placeholder="Client / workshop name" aria-label="Client or workshop name" style="margin-bottom:8px">
        <button class="btn btn--sm" style="width:100%;justify-content:center;background:rgba(255,255,255,.08);color:rgba(255,255,255,.85)" id="sess-create">＋ Create new session</button>
      </div>
      <div class="overlay__card">
        <h2 class="overlay__card-heading">📋 Your sessions</h2>
        <div id="sess-directory"><p class="muted" style="font-size:11px">Loading…</p></div>
      </div>
      ` : ''}
    `;
    el.querySelector('#sess-join').addEventListener('click', () => this.#joinSession());
    el.querySelector('#sess-code').addEventListener('keydown', e => { if (e.key === 'Enter') this.#joinSession(); });
    if (canFacilitate) {
      el.querySelector('#sess-create').addEventListener('click', () => this.#createSession());
      el.querySelector('#sess-clientname').addEventListener('keydown', e => { if (e.key === 'Enter') this.#createSession(); });
      this.#loadSessionDirectory();
    }
  }

  async #joinSession() {
    const raw  = document.getElementById('sess-code')?.value ?? '';
    const code = normaliseSessionCode(raw);
    if (!code) {
      this.#mountSessionOverlay(`Enter the ${SESSION_CODE_LENGTH}-character code exactly as announced.`);
      return;
    }
    const btn = document.getElementById('sess-join');
    if (btn) btn.disabled = true;
    try {
      if (!(await firebase.sessionExists(code))) {
        this.#mountSessionOverlay(`No session found with code ${code} — check it with your facilitator.`);
        return;
      }
      await this.#enterSession(code);
    } catch (e) {
      console.error('[App] join session failed:', e);
      this.#mountSessionOverlay('Could not join — check your connection and try again.');
    }
  }

  async #createSession() {
    const clientName = document.getElementById('sess-clientname')?.value.trim() ?? '';
    if (!clientName) {
      this.#mountSessionOverlay('Enter a client / workshop name for this session.');
      return;
    }
    const btn = document.getElementById('sess-create');
    if (btn) btn.disabled = true;
    try {
      // Regenerate on the (vanishingly rare) code collision
      for (let attempt = 0; attempt < 5; attempt++) {
        const code = generateSessionCode();
        if (await firebase.createSession(code, clientName)) {
          await this.#enterSession(code);
          this.#showToast(`Session created — join code ${code}`, 'success');
          this.#notifySessionCreated(code, clientName);
          return;
        }
      }
      this.#mountSessionOverlay('Could not create a session — please try again.');
    } catch (e) {
      console.error('[App] create session failed:', e);
      this.#mountSessionOverlay('Could not create a session — check your connection and try again.');
    }
  }

  /**
   * Best-effort email to the facilitator who just created this session, via
   * the /api/notify-session Cloudflare Pages Function. Never blocks or fails
   * session creation — no such endpoint locally (vite dev doesn't run Pages
   * Functions), and on a deployment missing RESEND_API_KEY or Access config
   * it just 403s/500s; either way this only logs a warning.
   */
  #notifySessionCreated(code, clientName) {
    fetch('/api/notify-session', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, clientName }),
    })
      .then(res => { if (!res.ok) console.warn('[App] session-notify email failed:', res.status); })
      .catch(e => console.warn('[App] session-notify request failed:', e));
  }

  // ── Facilitator session directory ───────────────────────────────────────────
  #sessionList = [];
  #pendingDeleteCode = null;
  #pendingDeleteTimer = null;

  async #loadSessionDirectory() {
    const container = document.getElementById('sess-directory');
    if (!container) return;
    try {
      this.#sessionList = await firebase.listSessions();
    } catch (e) {
      console.error('[App] list sessions failed:', e);
      container.innerHTML = '<p class="overlay__error" style="font-size:11px">Could not load sessions.</p>';
      return;
    }
    this.#renderDirectoryInto(container);
  }

  #renderDirectoryInto(container) {
    container.innerHTML = this.#sessionList.length
      ? this.#sessionList.map(s => this.#directoryRow(s)).join('')
      : '<p class="muted" style="font-size:11px">No sessions yet.</p>';
    container.querySelectorAll('[data-switch]').forEach(btn =>
      btn.addEventListener('click', () => this.#enterSession(btn.dataset.switch)));
    container.querySelectorAll('[data-delete]').forEach(btn =>
      btn.addEventListener('click', () => this.#onDeleteClick(btn.dataset.delete)));
  }

  #directoryRow(s) {
    const label = s.clientName ? escHtml(s.clientName) : '<span class="muted">(untitled)</span>';
    const when  = new Date(s.createdTs).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
    const armed = this.#pendingDeleteCode === s.code;
    return `
      <div style="display:flex;align-items:center;gap:8px;padding:6px 0;border-bottom:1px solid rgba(255,255,255,.08)">
        <div style="flex:1;min-width:0;overflow:hidden">
          <div style="font-size:12px;font-weight:700;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${label}</div>
          <div class="muted" style="font-size:10px;letter-spacing:1px">${escHtml(s.code)} · ${when}</div>
        </div>
        <button class="btn btn--sm" data-switch="${escHtml(s.code)}" style="padding:4px 8px;font-size:11px;flex-shrink:0">Switch</button>
        <button class="btn btn--sm" data-delete="${escHtml(s.code)}" style="padding:4px 8px;font-size:11px;flex-shrink:0;${armed ? 'background:#7a1f1f;color:#fff' : ''}">${armed ? 'Confirm?' : 'Delete'}</button>
      </div>`;
  }

  #onDeleteClick(code) {
    const container = document.getElementById('sess-directory');
    if (this.#pendingDeleteCode === code) {
      clearTimeout(this.#pendingDeleteTimer);
      this.#pendingDeleteCode = null;
      firebase.deleteSession(code)
        .then(() => { this.#showToast('Session deleted.', 'success'); this.#loadSessionDirectory(); })
        .catch(e => { console.error('[App] delete session failed:', e); this.#showToast('Could not delete session.', 'error'); });
      return;
    }
    this.#pendingDeleteCode = code;
    clearTimeout(this.#pendingDeleteTimer);
    this.#pendingDeleteTimer = setTimeout(() => {
      this.#pendingDeleteCode = null;
      if (container) this.#renderDirectoryInto(container);
    }, 4000);
    if (container) this.#renderDirectoryInto(container);
  }

  /** Leave the current session (role and team are reset — a different
   *  workshop means a different identity). */
  #switchSession() {
    this.#stopTimer();
    this.#unsub?.();
    this.#unsub = null;
    storage.remove('mv_session');
    storage.remove('mv_draft');
    this.#localStore.setState({ ...initialLocalState });
    saveLocal(this.#localStore.getState(), storage);
    this.#showOverlay('session');
  }

  /**
   * Whether this device is allowed to pick Facilitator/Master roles.
   * `access` is populated by the `/firebase-config` Pages Function from a
   * verified Cloudflare Access identity; its absence (local dev, manual
   * config entry, deployments without Access gating configured) means the
   * restriction isn't configured, so we leave selection open as before.
   */
  #canFacilitate() {
    const access = window.__FIREBASE_CFG__?.access;
    return access ? access.canFacilitate : true;
  }

  #mountRoleOverlay() {
    const el = document.getElementById('overlay-role');
    if (!el) return;
    const canFacilitate = this.#canFacilitate();
    el.innerHTML = `
      <div class="overlay__logo">${escHtml(BRAND.simName)}</div>
      <h1 class="overlay__title">Who are you?</h1>
      <div class="overlay__card">
        <h2 class="overlay__card-heading">Select your role</h2>
        <div class="role-grid" role="group" aria-label="Role selection">
          <button class="role-btn" data-role="team"        aria-label="Team iPad">📋 Team iPad</button>
          ${canFacilitate ? `<button class="role-btn" data-role="facilitator" aria-label="Lead Facilitator">🎯 Lead Facilitator</button>` : ''}
          <button class="role-btn" data-role="scorer"      aria-label="Scorer">✏️ Scorer</button>
          <button class="role-btn" data-role="board"       aria-label="Licence Board">📊 Licence Board</button>
        </div>
        ${canFacilitate ? `
        <div style="margin-top:10px;border-top:1px solid rgba(255,255,255,.1);padding-top:10px">
          <h2 class="overlay__card-heading">🔐 Master access</h2>
          <button class="btn btn--primary btn--sm" style="width:100%;justify-content:center" id="masterBtn">Enter as master</button>
        </div>
        ` : `
        <p class="muted" style="font-size:11px;margin-top:10px">Lead Facilitator and Master access are restricted to authorised facilitator emails. Contact your workshop lead if you believe this is incorrect.</p>
        `}
      </div>
      <p style="font-size:11px;color:rgba(255,255,255,.45);margin-top:14px">
        Session <strong style="color:rgba(255,255,255,.75);letter-spacing:1px">${escHtml(firebase.getSessionCode() ?? '')}</strong>
        · <button id="sessSwitch" style="background:none;border:none;color:var(--brand-primary);cursor:pointer;font-size:11px;text-decoration:underline;padding:0">switch session</button>
      </p>
    `;
    el.querySelectorAll('.role-btn').forEach(btn => {
      btn.addEventListener('click', () => this.#pickRole(btn.dataset.role));
    });
    if (canFacilitate) {
      el.querySelector('#masterBtn').addEventListener('click', () => this.#tryMaster());
    }
    el.querySelector('#sessSwitch').addEventListener('click', () => this.#switchSession());
  }

  #pickRole(role) {
    if (role === 'facilitator' && !this.#canFacilitate()) return;
    if (role === 'scorer') { this.#mountScorerNumPicker(); return; }
    if (role === 'team') { this.#showOverlay('privacy'); return; }
    const L = { ...initialLocalState, role, isMaster: false };
    this.#localStore.setState(L);
    saveLocal(L, storage);
    document.getElementById('overlay-role').hidden = true;
    document.getElementById('app-shell').hidden = false;
    this.#applyRole();
  }

  /**
   * Privacy statement shown before Team Setup — must be accepted before
   * a team device can register. Declining returns to role selection
   * without setting `role: 'team'` on this device.
   */
  #mountPrivacyOverlay() {
    const el = document.getElementById('overlay-privacy');
    if (!el) return;
    el.innerHTML = `
      <div class="overlay__logo">${escHtml(BRAND.simName)}</div>
      <h1 class="overlay__title">Privacy Statement</h1>
      <div class="overlay__card" style="color:rgba(255,255,255,.85)">
        ${PRIVACY.split(/\n{2,}/).map((p, i) => `
        <p style="font-size:13px;line-height:1.6${i > 0 ? ';margin-top:10px' : ''}">${escHtml(p)}</p>`).join('')}
      </div>
      <div style="max-width:500px;width:100%;display:flex;gap:10px">
        <button class="btn btn--sm" id="privacyBack" style="flex:1;justify-content:center;background:rgba(255,255,255,.08);color:rgba(255,255,255,.7)">← Back</button>
        <button class="btn btn--primary" id="privacyAccept" style="flex:2;justify-content:center;padding:12px;font-size:14px">I Accept</button>
      </div>
    `;
    el.querySelector('#privacyAccept').addEventListener('click', () => this.#acceptPrivacy());
    el.querySelector('#privacyBack').addEventListener('click', () => this.#showOverlay('role'));
  }

  #acceptPrivacy() {
    const L = { ...initialLocalState, role: 'team', isMaster: false };
    this.#localStore.setState(L);
    saveLocal(L, storage);
    this.#showOverlay('team');
  }

  /** Scorers must pick which of the 9 scorer numbers they are — each is assigned a fixed
   *  team via SCORER_TEAMS. Without this step every scorer device defaulted to
   *  scorerNum 1, so multiple scorers ended up scoring the same team. */
  #mountScorerNumPicker() {
    const el = document.getElementById('overlay-role');
    if (!el) return;
    const G = this.#gameStore.getState();
    const opts = Object.entries(SCORER_TEAMS).map(([num, teams]) => {
      const labels = teams.map(t => teamLabel(t, G.teams[fk(t)]?.displayName)).join(', ');
      return `<button class="role-btn" data-scorer-num="${num}" aria-label="Scorer ${num} — scoring ${escHtml(labels)}" style="text-align:left">
        <span style="display:block;font-size:14px">✏️ Scorer ${num}</span>
        <span style="display:block;font-size:10px;font-weight:400;color:rgba(255,255,255,.5);margin-top:2px">${escHtml(labels)}</span>
      </button>`;
    }).join('');
    el.innerHTML = `
      <div class="overlay__logo">${escHtml(BRAND.simName)}</div>
      <h1 class="overlay__title">Which scorer are you?</h1>
      <div class="overlay__card">
        <h2 class="overlay__card-heading">Select your scorer number</h2>
        <p class="muted" style="font-size:11px;margin-bottom:10px">Each scorer number is assigned a fixed team — pick the one your facilitator gave you.</p>
        <div class="role-grid" role="group" aria-label="Scorer number selection" style="grid-template-columns:1fr">${opts}</div>
        <button class="btn btn--sm" id="scorerBack" style="margin-top:10px;width:100%;justify-content:center;background:rgba(255,255,255,.08);color:rgba(255,255,255,.7)">← Back to role selection</button>
      </div>
    `;
    el.querySelectorAll('[data-scorer-num]').forEach(btn => {
      btn.addEventListener('click', () => this.#finishScorerPick(parseInt(btn.dataset.scorerNum, 10)));
    });
    el.querySelector('#scorerBack').addEventListener('click', () => this.#mountRoleOverlay());
  }

  #finishScorerPick(num) {
    const L = { ...initialLocalState, role: 'scorer', isMaster: false, scorerNum: num };
    this.#localStore.setState(L);
    saveLocal(L, storage);
    document.getElementById('overlay-role').hidden = true;
    document.getElementById('app-shell').hidden = false;
    this.#applyRole();
  }

  #tryMaster() {
    if (!this.#canFacilitate()) return;
    const L = { ...initialLocalState, role: 'master', isMaster: true };
    this.#localStore.setState(L);
    saveLocal(L, storage);
    document.getElementById('overlay-role').hidden = true;
    document.getElementById('app-shell').hidden = false;
    this.#applyRole();
  }

  #mountTeamOverlay() {
    const el  = document.getElementById('overlay-team');
    const L   = this.#localStore.getState();
    const G   = this.#gameStore.getState();
    if (!el) return;
    const wasReturned = !!(L.team && !G.teams?.[fk(L.team)]);
    const memberInputs = [0,1,2,3,4,5,6,7,8].map((i) => {
      const optional = i >= 4;
      return `<div class="member-row">
        <span class="member-num" aria-hidden="true">${i + 1}</span>
        <input class="overlay__input" style="margin:0" placeholder="First name${optional ? ' (optional)' : ''}"
          id="m${i}" value="${escHtml(L.members[i] ?? '')}" aria-label="Member ${i+1} name" ${optional ? '' : 'aria-required="true"'}>
      </div>`;
    }).join('');

    el.innerHTML = `
      <div class="overlay__logo">${escHtml(BRAND.simName)}</div>
      <h1 class="overlay__title">Team Setup</h1>
      ${wasReturned ? `
      <div class="overlay__card" style="background:rgba(255,193,7,.12);border-color:rgba(255,193,7,.4)">
        <p style="font-size:12px;line-height:1.5">↩ Your facilitator has returned your registration — review your team name and members below, then resubmit to rejoin the simulation.</p>
      </div>
      ` : ''}
      <div class="overlay__card">
        <h2 class="overlay__card-heading">Your team name</h2>
        <input class="overlay__input" id="tNameIn" type="text" maxlength="40" autocomplete="off"
          value="${escHtml(L.displayName ?? '')}"
          aria-label="Team name" aria-required="true">
        <p class="muted" style="font-size:11px;margin-top:6px">Pick a name for your team.</p>
      </div>
      <div class="overlay__card">
        <h2 class="overlay__card-heading">Team members <span style="color:rgba(255,255,255,.35);font-weight:400">(minimum 4, max 9)</span></h2>
        <div id="memberInputs">${memberInputs}</div>
      </div>
      <div style="max-width:480px;width:100%">
        <button class="btn btn--primary" style="width:100%;justify-content:center;padding:12px;font-size:14px" id="teamSubmit">Enter the simulation</button>
        <p class="overlay__error" id="teamErr" role="alert" hidden>Enter a team name and at least 4 member names.</p>
      </div>
    `;
    el.querySelector('#teamSubmit').addEventListener('click', () => this.#doTeamSetup());
    el.querySelector('#tNameIn').addEventListener('keydown', e => { if (e.key === 'Enter') el.querySelector('#m0')?.focus(); });
  }

  async #doTeamSetup() {
    const displayName = document.getElementById('tNameIn')?.value.trim() ?? '';
    const members = [0,1,2,3,4,5,6,7,8]
      .map(i => document.getElementById(`m${i}`)?.value.trim() ?? '')
      .filter(Boolean);
    const { ok } = validateTeamSetup({ name: displayName, members });
    if (!ok) {
      const err = document.getElementById('teamErr');
      if (err) { err.textContent = 'Enter a team name and at least 4 member names.'; err.hidden = false; }
      return;
    }

    const prior = this.#localStore.getState();
    let canonical = prior.team;
    try {
      if (canonical) {
        // Already registered on this device — keep the same station, just update details
        await firebase.writeGame(`teams/${fk(canonical)}`, { name: canonical, displayName, members, ts: Date.now() });
      } else {
        // First-time registration — claim the next free station in order
        canonical = await firebase.claimTeamSlot(TEAM_NAMES, { displayName, members });
        if (!canonical) {
          const err = document.getElementById('teamErr');
          if (err) { err.textContent = 'All team stations are full — ask your facilitator for help.'; err.hidden = false; }
          return;
        }
      }
    } catch {
      const err = document.getElementById('teamErr');
      if (err) { err.textContent = 'Could not register your team — check your connection and try again.'; err.hidden = false; }
      return;
    }

    const L = { ...prior, role: 'team', team: canonical, displayName, members };
    this.#localStore.setState(L);
    saveLocal(L, storage);
    document.getElementById('overlay-team').hidden = true;
    document.getElementById('app-shell').hidden = false;
    this.#applyRole();
  }

  /**
   * Content editor — rendered in its own overlay, outside the game-state
   * re-render cycle, so live game updates can't wipe in-progress edits.
   * Reached via the Facilitator "Content" tab (Cloudflare Access gated,
   * same as the facilitator role itself).
   */
  #contentEditor = null;

  #mountContentEditor() {
    const el = document.getElementById('overlay-contentpack');
    if (!el) return;
    this.#contentEditor?.destroy();
    this.#contentEditor = new ContentEditor(el, {
      firebase,
      onClose: () => {
        this.#contentEditor?.destroy();
        this.#contentEditor = null;
        el.hidden = true;
        document.getElementById('app-shell').hidden = false;
        applyBrand();
        this.#applyRole(); // re-render nav + views with possibly-updated content
      },
    });
    this.#contentEditor.mount();
  }

  // ── Master switcher ───────────────────────────────────────────────────────
  #setupMasterSwitcher(L) {
    const sw = document.getElementById('master-switcher');
    if (!sw || !L.isMaster) { if (sw) sw.hidden = true; return; }
    sw.hidden = false;
    const roles = [
      { id:'facilitator',label:'🎯 Facilitator' },
      ...Object.keys(SCORER_TEAMS).map(n => ({ id:`scorer-${n}`, label:`✏️ Scorer ${n}` })),
      { id:'board',label:'📊 Licence Board' },
      { id:'__switch-session',label:'🔁 Switch session' },
    ];
    sw.innerHTML = `
      <div class="master-switcher__panel" id="mswPanel" hidden role="menu">
        ${roles.map(r => `<button class="master-switcher__option" role="menuitem" data-msw="${r.id}">${r.label}</button>`).join('')}
      </div>
      <button class="master-switcher__toggle" aria-haspopup="true" aria-expanded="false" id="mswToggle">⚙ MASTER</button>
    `;
    const panel  = sw.querySelector('#mswPanel');
    const toggle = sw.querySelector('#mswToggle');
    toggle.addEventListener('click', () => {
      panel.hidden = !panel.hidden;
      toggle.setAttribute('aria-expanded', !panel.hidden);
    });
    sw.querySelectorAll('.master-switcher__option').forEach(btn => {
      btn.addEventListener('click', () => {
        panel.hidden = true;
        toggle.setAttribute('aria-expanded', false);
        const id = btn.dataset.msw;
        if (id === '__switch-session') {
          this.#switchSession();
        } else if (id.startsWith('scorer-')) {
          const num = parseInt(id.split('-')[1], 10);
          const L2  = { ...this.#localStore.getState(), scorerNum: num };
          this.#localStore.setState(L2);
          saveLocal(L2, storage);
          this.#showView('scorer');
        } else {
          this.#showView(id);
        }
      });
    });
  }

  // ── Connection indicator ──────────────────────────────────────────────────
  /** Driven by Firebase's `.info/connected`. Offline is only shown after a
   *  2s grace period (reconnect blips are routine) and only once the device
   *  has been online — the initial handshake always starts disconnected. */
  #updateConnStatus(connected) {
    const el = document.getElementById('conn-status');
    if (!el) return;
    clearTimeout(this.#connOfflineTimer);
    this.#connOfflineTimer = null;
    if (connected) {
      this.#connEverOnline = true;
      el.hidden = false;
      el.className = 'conn-status conn-status--online';
      el.innerHTML = '<span class="conn-status__dot" aria-hidden="true"></span>Live';
    } else if (this.#connEverOnline) {
      this.#connOfflineTimer = setTimeout(() => {
        el.hidden = false;
        el.className = 'conn-status conn-status--offline';
        el.innerHTML = '<span class="conn-status__dot" aria-hidden="true"></span>Offline — reconnecting…';
      }, 2000);
    }
  }

  // ── Loading / error UI ────────────────────────────────────────────────────
  #showLoading(visible) {
    const el = document.getElementById('app-loading');
    if (el) el.hidden = !visible;
  }

  #showToast(msg, variant = '') {
    const toast = document.createElement('div');
    toast.className = `toast${variant ? ` toast--${variant}` : ''}`;
    toast.textContent = msg;
    toast.setAttribute('role', 'alert');
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 4000);
  }

  #setupErrorHandler() {
    window.addEventListener('unhandledrejection', e => {
      console.error('[App] unhandled rejection:', e.reason);
    });
  }

  destroy() {
    this.#stopTimer();
    clearTimeout(this.#connOfflineTimer);
    if (this.#unsub) this.#unsub();
    if (this.#unsubConn) this.#unsubConn();
    Object.values(this.#views).forEach(v => v.destroy());
    this.#nav?.destroy();
  }
}

function escHtml(s) {
  return String(s ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#039;');
}
